import { lazy, Suspense } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import Notification from '@/lib/components/notification'
import ServicesNav from '@/lib/components/layout/services-nav'


// Layouts
import LandingLayout from '@/lib/components/layout/landing-layout'
import LayoutDashboard from '@/lib/components/layout/layout-dashboard'
import OrderLayout from '@/lib/components/layout/order-layout'

// Landing Pages
const HomePage = lazy(() => import('@/pages/home'))
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'))
const RefundPolicyPage = lazy(() => import('@/pages/refund-policy'))
const TermsOfUsePage = lazy(() => import('@/pages/terms-of-use'))

// App Pages
const CreateOrderPage = lazy(() => import('@/pages/create-order'))
const YoutubePage = lazy(() => import('@/pages/youtube'))
const TiktokSellPage = lazy(() => import('@/pages/tiktoksellpage'))
const SpotifyPage = lazy(() => import('@/pages/spotify'))
const SelectPostPage = lazy(() => import('@/pages/select-post'))
const DashboardPage = lazy(() => import('@/pages/dashboard'))
const CompleteOrderPage = lazy(() => import('@/pages/complete-order'))
const CompleteOrderDetailPage = lazy(
    () => import('@/pages/complete-order/detail')
)

// Payment Pages
const PaymentPage = lazy(() => import('@/pages/payment'))
const PaymentSuccessPage = lazy(() => import('@/pages/payment/success'))
const PaymentCancelPage = lazy(() => import('@/pages/payment/cancel'))
const PaymentRedirectPage = lazy(() => import('@/pages/payment/redirect'))
const GbpayCardPage = lazy(() => import('@/pages/payment/gbpay-card'))

const Loading = () => (
    <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent" />
    </div>
)

function GlobalServicesNav() {
    const { pathname } = useLocation()
    // Hide on landing/policy routes — LandingHeader already shows service links there.
    const hide = ['/', '/privacy-policy', '/refund-policy', '/terms-of-use'].includes(pathname)
    if (hide) return null
    const darkRoutes = ['/tiktoksellpage', '/youtube', '/spotify']
    const isDark = darkRoutes.some((r) => pathname.startsWith(r))
    return <ServicesNav dark={isDark} />
}

export default function App() {
    return (
        <>
            <Suspense fallback={<Loading />}>
                <GlobalServicesNav />
                <Routes>

                    {/* Landing pages */}
                    <Route path="/" element={<HomePage />} />

                    <Route
                        path="/privacy-policy"
                        element={
                            <LandingLayout>
                                <PrivacyPolicyPage />
                            </LandingLayout>
                        }
                    />
                    <Route
                        path="/refund-policy"
                        element={
                            <LandingLayout>
                                <RefundPolicyPage />
                            </LandingLayout>
                        }
                    />
                    <Route
                        path="/terms-of-use"
                        element={
                            <LandingLayout>
                                <TermsOfUsePage />
                            </LandingLayout>
                        }
                    />

                    {/* App pages */}
                    <Route
                        path="/create-order"
                        element={<CreateOrderPage />}
                    />
                    <Route path="/youtube" element={<YoutubePage />} />
                    <Route path="/tiktoksellpage" element={<TiktokSellPage />} />
                    <Route path="/spotify" element={<SpotifyPage />} />
                    <Route
                        path="/select-post"
                        element={
                            <OrderLayout>
                                <SelectPostPage />
                            </OrderLayout>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <LayoutDashboard>
                                <DashboardPage />
                            </LayoutDashboard>
                        }
                    />

                    {/* Complete order */}
                    <Route
                        path="/complete-order"
                        element={
                            <OrderLayout>
                                <CompleteOrderPage />
                            </OrderLayout>
                        }
                    />
                    <Route
                        path="/complete-order/:id"
                        element={
                            <OrderLayout hasBackButton>
                                <CompleteOrderDetailPage />
                            </OrderLayout>
                        }
                    />

                    {/* Payment pages */}
                    <Route path="/payment" element={<PaymentPage />} />
                    <Route
                        path="/payment/success"
                        element={<PaymentSuccessPage />}
                    />
                    <Route
                        path="/payment/cancel"
                        element={<PaymentCancelPage />}
                    />
                    <Route
                        path="/payment/redirect"
                        element={<PaymentRedirectPage />}
                    />
                    <Route
                        path="/payment/gbpay-card"
                        element={<GbpayCardPage />}
                    />
                    <Route
                        path="/payment/gbpay-card/return"
                        element={<PaymentSuccessPage />}
                    />
                </Routes>
            </Suspense>
            <Notification />
        </>
    )
}
